import { useCallback, useEffect, useMemo, useState } from "react";
import { reportsService, groupService } from "@/services";
import type { AuctionHistoryData, KametiGroup } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";
import { exportReportCSV, csvAmount } from "@/utils/report";

export default function AuctionHistoryPage() {
  const [range, setRange] = useState<DateRange>(() => {
    const now = new Date();
    return { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), end: now.toISOString() };
  });
  const [groupId, setGroupId] = useState("");
  const [data, setData] = useState<AuctionHistoryData | null>(null);
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, g] = await Promise.all([
        reportsService.getAuctionHistory({ range, groupId }),
        groupService.listGroups(),
      ]);
      setData(d);
      setGroups(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load auction history.");
      setData(null);
    }
  }, [range, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (range.start) parts.push(`From ${formatDate(range.start)}`);
    if (range.end) parts.push(`To ${formatDate(range.end)}`);
    if (groupId) parts.push(groups.find((g) => g.id === groupId)?.name ?? "Group");
    return parts.length ? parts.join(" · ") : "All auctions";
  }, [range, groupId, groups]);

  const handleExport = () => {
    if (!data) return;
    exportReportCSV({
      filename: "auction-history",
      reportName: "Auction History Report",
      filters: [filterSummary],
      columns: [
        { header: "Group", value: (r) => r.group },
        { header: "Cycle", value: (r) => r.cycle },
        { header: "Period", value: (r) => r.period },
        { header: "Date", value: (r) => r.date },
        { header: "Winner", value: (r) => r.winner },
        { header: "Ticket", value: (r) => r.ticket },
        { header: "Pool", value: (r) => csvAmount(r.pool) },
        { header: "Winning bid", value: (r) => csvAmount(r.bid) },
        { header: "Discount", value: (r) => csvAmount(r.discount) },
        { header: "Discount %", value: (r) => `${r.discountPct}%` },
        { header: "Dividend / member", value: (r) => csvAmount(r.dividend) },
      ],
      rows: data.rows.map((r) => ({
        group: `${r.groupName} (${r.groupCode})`,
        cycle: r.cycle,
        period: r.periodLabel,
        date: formatDate(r.scheduledDate),
        winner: r.winnerName,
        ticket: r.winnerTicket,
        pool: r.pool,
        bid: r.winningBid,
        discount: r.discount,
        discountPct: r.discountPct.toFixed(1),
        dividend: r.perMemberDividend,
      })),
    });
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Auction history" isLast />
          </>
        }
        title="Auction history"
        subtitle="All auctions across groups — winners, bids, discounts and dividends."
        actions={
          <Button variant="outline" size="sm" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
            Export
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <DateRangePicker value={range} onChange={setRange} />
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : data.rows.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <EmptyState icon="ri-hammer-line" title="No closed auctions match" description="Try a wider date range, or check that auctions have been held for the selected group." />
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Auctions held</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-foreground-950">{data.rows.length}</p>
              </div>
            </Card>
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Avg discount</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-foreground-950">{formatINR(data.avgDiscount)}</p>
              </div>
            </Card>
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Avg discount %</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-foreground-950">{data.avgDiscountPct.toFixed(1)}%</p>
              </div>
            </Card>
          </div>

          <Card>
            <div className="overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-3 py-2 text-left">Group</th>
                    <th className="px-3 py-2 text-left">Cycle</th>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Winner</th>
                    <th className="px-3 py-2 text-right">Pool</th>
                    <th className="px-3 py-2 text-right">Winning bid</th>
                    <th className="px-3 py-2 text-right">Discount</th>
                    <th className="px-3 py-2 text-right">Disc. %</th>
                    <th className="px-3 py-2 text-right">Dividend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={`${r.groupId}-${r.cycle}`} className="border-t border-background-100 hover:bg-background-100/50">
                      <td className="px-3 py-2 font-medium text-foreground-900">{r.groupName}<span className="tnum ml-1 text-xs text-foreground-400">{r.groupCode}</span></td>
                      <td className="px-3 py-2">
                        <span className="tnum">{r.cycle}</span>
                        {r.isOrganizerReserved ? <Badge color="accent" className="ml-1">Reserved</Badge> : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-foreground-700">{r.periodLabel}</td>
                      <td className="tnum whitespace-nowrap px-3 py-2 text-foreground-600">{formatDate(r.scheduledDate)}</td>
                      <td className="px-3 py-2 text-foreground-900">{r.winnerName} <span className="tnum text-xs text-foreground-400">{r.winnerTicket}</span></td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={r.pool} hideSymbol /></td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={r.winningBid} hideSymbol className="font-medium" /></td>
                      <td className="px-3 py-2 text-right text-success-600"><CurrencyDisplay amount={r.discount} hideSymbol /></td>
                      <td className="tnum px-3 py-2 text-right text-foreground-700">{r.discountPct.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={r.perMemberDividend} hideSymbol /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}